import React from 'react';
import { TableCell, TableRow as MURow, IconButton, Chip } from '@mui/material';
import { color } from '../../theme';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import EditIcon from '../../static/edit.svg';
import { UserRole } from '../../utils/constants';
import { Subscription, User } from 'types';
import { getUserRole } from 'utils/userUtils';

const formatDate = (value?: string | null) => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString('fi-FI');
};

const findSubscriptionById = (subscriptions: User['subscriptions'] | undefined, subscriptionId?: number | null) => {
  if (!subscriptions || subscriptions.length === 0) return undefined;
  if (!subscriptionId) return undefined;
  return subscriptions.find((s) => s.id === subscriptionId);
};

const useStyles = makeStyles()({
  root: {
    borderBottom: `1px solid ${color.greyBorderLight}`,
    display: 'flex',
    justifyContent: 'space-between',
    justifyItems: 'flex-start',
    width: '100%',
    maxWidht: 1000,
  },
  cell: {
    display: 'flex',
    alignSelf: 'flex-start',
    fontSize: '18px',
    color: color.black,
    fontWeight: 400,
    lineHeight: '27px',
    padding: 0,
    border: 0,
    marginTop: 25,
    minWidth: 300,
    textTransform: 'capitalize',
    textAlign: 'left',
  },
  cellEmail: {
    display: 'flex',
    alignSelf: 'flex-start',
    fontSize: '12px',
    color: color.black,
    fontWeight: 400,
    lineHeight: '27px',
    padding: 0,
    border: 0,
    marginTop: 25,
    minWidth: 300,
    textTransform: 'capitalize',
    textAlign: 'left',
  },
  cellUser: {
    display: 'flex',
    alignSelf: 'flex-start',
    fontSize: '18px',
    color: color.black,
    fontWeight: 400,
    lineHeight: '27px',
    padding: 0,
    border: 0,
    marginTop: 25,
    minWidth: 200,
    textTransform: 'capitalize',
    textAlign: 'left',
  },
  btnCell: {
    border: 0,
    minWidth: 100,
    maxWidth: 100,
  },
  portalRow: {
    borderBottom: `1px solid ${color.greyBorderLight}`,
    display: 'grid',
    width: '100%',
    alignItems: 'center',
    padding: '12px 0',
    gap: '8px',
  },
  portalCell: {
    border: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    color: color.black,
    textTransform: 'none',
  },
  portalPrimary: {
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  portalSecondary: {
    fontSize: '13px',
    color: color.black,
    textTransform: 'none',
  },
  portalBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  portalRoleCell: {
    border: 0,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  badgeChip: {
    textTransform: 'none',
  },
});

interface Props {
  user: User;
  userRole: UserRole;
  columnTemplate?: string;
  onFixMissingEndDate?: (user: User, subscription: Subscription) => void;
}

function UsersTableRow({ user, userRole, columnTemplate, onFixMissingEndDate }: Props) {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isPortalView = Boolean(columnTemplate);
  const hasActiveSubscription = user.activeSubscriptionId !== null && user.activeSubscriptionId !== undefined;
  const activeSubscriptionRecord = findSubscriptionById(user.subscriptions, user.activeSubscriptionId);

  const activePlanLabel = (user.activeSubscriptionTypeName ?? '').trim() || '-';
  const latestPlanLabel = (user.latestSubscriptionTypeName ?? '').trim() || '';

  const platformRaw = (user.activeSubscriptionPlatform ?? user.latestSubscriptionPlatform ?? '').trim();
  const platformValue = platformRaw.toLowerCase();
  const platformLabel = platformValue ? t(`portal_users.filters.${platformValue}`, { defaultValue: platformRaw }) : '-';

  const periodEndsLabel = activeSubscriptionRecord?.periodEnds ? formatDate(activeSubscriptionRecord.periodEnds) : '';
  const missingEndDate = Boolean(
    hasActiveSubscription && activeSubscriptionRecord && !activeSubscriptionRecord.periodEnds
  );
  const handleFixMissingEndDate = () => {
    if (!onFixMissingEndDate || !activeSubscriptionRecord) {
      return;
    }
    onFixMissingEndDate(user, activeSubscriptionRecord as Subscription);
  };

  const editRow = () => {
    if (userRole === UserRole.admin || userRole === UserRole.publisher || userRole === UserRole.creator) {
      navigate(`/admin-users/${user.id}`);
    } else if (userRole == UserRole.employee) {
      navigate(`/emp-users/${user.id}`);
    } else {
      navigate(`/portal-users/${user.id}`);
    }
  };

  return (
    <MURow
      className={isPortalView ? classes.portalRow : classes.root}
      style={isPortalView ? { gridTemplateColumns: columnTemplate ?? '35% 23% 12% 12% 18%' } : undefined}
    >
      {isPortalView ? (
        <>
          <TableCell className={classes.portalCell}>
            <span className={classes.portalPrimary}>{`${user.lastname}, ${user.firstname}`}</span>
            <span className={classes.portalSecondary}>{user.email}</span>
          </TableCell>
          <TableCell className={classes.portalCell}>
            <span className={classes.portalPrimary}>{activePlanLabel}</span>
            {!hasActiveSubscription && latestPlanLabel && (
              <span className={classes.portalSecondary}>{latestPlanLabel}</span>
            )}
            {missingEndDate && (
              <div className={classes.portalBadges}>
                <Chip
                  size="small"
                  color="warning"
                  label={t('portal_users.badge.missing_end_date')}
                  className={classes.badgeChip}
                  clickable
                  onClick={handleFixMissingEndDate}
                  aria-label={t('portal_users.fix_missing_end_date_badge_aria')}
                />
              </div>
            )}
          </TableCell>
          <TableCell className={classes.portalCell}>
            <span className={classes.portalPrimary}>{platformLabel}</span>
          </TableCell>
          <TableCell className={classes.portalCell}>
            <span className={classes.portalPrimary}>
              {periodEndsLabel || (hasActiveSubscription ? t('portal_users.period_missing') : '-')}
            </span>
          </TableCell>
          <TableCell className={classes.portalRoleCell}>
            <span className={classes.portalPrimary}>{t(getUserRole(user))}</span>
            <IconButton onClick={editRow}>
              <img src={EditIcon} alt="Edit" />
            </IconButton>
          </TableCell>
        </>
      ) : (
        <>
          <TableCell className={classes.cell}>{`${user.lastname}, ${user.firstname}`}</TableCell>
          <TableCell className={classes.cellEmail}>{`${user.email}`}</TableCell>
          <TableCell className={classes.cellUser}>{t(getUserRole(user))}</TableCell>
          <TableCell className={classes.btnCell}>
            <IconButton onClick={editRow}>
              <img src={EditIcon} alt="Edit" />
            </IconButton>
          </TableCell>
        </>
      )}
    </MURow>
  );
}

export default UsersTableRow;
